import { useMemo } from "react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { ZoomIn, ZoomOut, Locate } from "lucide-react";

// IMPORTANTE: Esta es la forma típica de importar un SVG como componente en Create React App.
// (Si usas Vite o Next.js, mira la nota abajo del código).
import { ReactComponent as MapComponent } from "../assets/svg/senderos.svg";

/**
 * Renders the SVG plot map with zoom + pan support.
 *
 * Props:
 * - lots: array of lots (viene de tu base de datos)
 * - onLotClick: (lot) => void
 * - selectedId: optional id to highlight
 */
export default function InteractiveMap({ lots, onLotClick, selectedId }) {
  
  // ----------------------------------------------------------------------
  // 1. ESTILOS INYECTADOS (Renderizado ultra-rápido de colores)
  // ----------------------------------------------------------------------
  const dynamicStyles = useMemo(() => {
    // Definimos los mismos colores que usas en tu leyenda (Tailwind palette)
    const colors = {
      available: "#10b981", // emerald-500
      reserved:  "#f59e0b", // amber-500
      sold:      "#ef4444", // red-500
    };

    // Recorremos los miles de lotes y generamos una regla CSS para cada ID
    const cssRules = (lots || []).map((lot) => {
      const color = colors[lot.status] || "#94a3b8"; // Gris por defecto si no hay estado
      
      return `
        /* Apuntamos al grupo Y a las figuras adentro (path, polygon, rect) */
        #${lot.id},
        #${lot.id} path,
        #${lot.id} polygon,
        #${lot.id} rect {
          fill: ${color} !important;
          transition: fill 0.3s ease, filter 0.2s ease;
        }
        
        /* Efecto hover también para los hijos */
        #${lot.id}:hover,
        #${lot.id}:hover path,
        #${lot.id}:hover polygon,
        #${lot.id}:hover rect {
          filter: brightness(1.15);
          cursor: pointer;
        }

        /* Resaltado para el elemento seleccionado */
        ${lot.id == selectedId ? `
          #${lot.id},
          #${lot.id} path,
          #${lot.id} polygon,
          #${lot.id} rect {
            stroke: #000000 !important;
            stroke-width: 3px !important;
            filter: saturate(1.5);
          }
        ` : ""}
      `;
    });

    return cssRules.join("\n");
  }, [lots, selectedId]);


  // ----------------------------------------------------------------------
  // 2. MANEJADOR DE CLICS
  // ----------------------------------------------------------------------
  const handleMapClick = (e) => {
    const clickedElement = e.target.closest('[id]');

    if (clickedElement && clickedElement.id) {
      // Limpiamos el ID del SVG: quitamos espacios y pasamos a minúsculas
      const clickedId = clickedElement.id.trim().toLowerCase();
      
      console.log("-----------------------------------------");
      console.log("📍 ID detectado en el SVG (limpio):", clickedId);
      
      // Buscamos en la lista de la BD aplicando la misma limpieza
      const foundLot = lots.find((lot) => {
        // const dbId = lot.id.trim().toLowerCase();
        const dbId = lot.id.trim().toLowerCase();
        return dbId === clickedId;
      });

      if (foundLot) {
        console.log("✅ ¡Match encontrado!", foundLot);
        if (onLotClick) onLotClick(foundLot);
      } else {
        console.warn(`❌ No hubo match.`);
        console.log("ID que buscaba:", clickedId);
        console.log("IDs disponibles en la BD actualmente:", lots.map(l => l.id));
      }
      console.log("-----------------------------------------");
    }
  };


  // ----------------------------------------------------------------------
  // 3. RENDERIZADO DEL COMPONENTE
  // ----------------------------------------------------------------------
  return (
    <div
      data-testid="interactive-map"
      className="relative w-full h-[60vh] sm:h-[70vh] min-h-[480px] border border-slate-200 bg-slate-50 rounded-lg overflow-hidden"
    >
      {/* ¡AQUÍ SE INYECTA EL CSS GENERADO! El navegador lo lee y pinta todo al instante */}
      <style>{dynamicStyles}</style>

      <TransformWrapper
        initialScale={1}
        minScale={0.6}
        maxScale={4}
        wheel={{ step: 0.15 }}
        doubleClick={{ disabled: false, step: 0.6 }}
      >
        {({ zoomIn, zoomOut, resetTransform }) => (
          <>
            <div className="absolute right-4 top-4 z-20 flex flex-col gap-2 bg-white border border-slate-200 rounded-md shadow-sm">
              <button
                data-testid="map-zoom-in"
                onClick={() => zoomIn()}
                className="p-2 hover:bg-slate-100 transition"
                aria-label="Acercar"
              >
                <ZoomIn size={16} />
              </button>
              <button
                data-testid="map-zoom-out"
                onClick={() => zoomOut()}
                className="p-2 hover:bg-slate-100 border-t border-slate-200 transition"
                aria-label="Alejar"
              >
                <ZoomOut size={16} />
              </button>
              <button
                data-testid="map-reset"
                onClick={() => resetTransform()}
                className="p-2 hover:bg-slate-100 border-t border-slate-200 transition"
                aria-label="Restablecer"
              >
                <Locate size={16} />
              </button>
            </div>

            <TransformComponent
              wrapperStyle={{ width: "100%", height: "100%" }}
              contentStyle={{ width: "100%", height: "100%" }}
            >
              {/* Aquí usamos el SVG como componente y le pasamos la función onClick */}
              <MapComponent
                className="w-full h-full object-contain cursor-pointer"
                onClick={handleMapClick}
              />
            </TransformComponent>
          </>
        )}
      </TransformWrapper>

      {/* Legend */}
      <div
        data-testid="map-legend"
        className="absolute left-4 bottom-4 z-20 flex flex-wrap gap-3 bg-white/95 backdrop-blur border border-slate-200 rounded-md px-3 py-2 text-xs"
      >
        <span className="inline-flex items-center gap-2">
          <span className="h-3 w-3 rounded-sm bg-emerald-500" /> Disponible
        </span>
        <span className="inline-flex items-center gap-2">
          <span className="h-3 w-3 rounded-sm bg-amber-500" /> Reservado
        </span>
        <span className="inline-flex items-center gap-2">
          <span className="h-3 w-3 rounded-sm bg-red-500" /> Vendido
        </span>
      </div>
    </div>
  );
}